import { apiClient } from './apiClient';

export interface AppointmentTypeShare {
    id: string;
    appointmentTypeId: string;
    userId: string;
    userEmail: string;
    userFullName: string;
    canWrite: boolean;
    createdAt: string;
}

export interface CreateAppointmentTypeShareDto {
    userEmail: string;
    canWrite: boolean;
}

export interface UpdateAppointmentTypeShareDto {
    canWrite: boolean;
}

export interface MyAppointmentTypeShare {
    id: string;
    appointmentTypeId: string;
    name: string;
    description: string;
    color: string;
    businessName: string;
    canWrite: boolean;
    sharedByUserId: string;
    sharedByUserName: string;
    createdAt: string;
}

export interface UpdateMyAppointmentTypeShareDto {
    canWrite: boolean;
}

export interface ShareRecipient {
    userId: string;
    fullName: string;
    canWrite: boolean;
}

export interface AppointmentTypeWithShares {
    id: string;
    name: string;
    color: string;
    businessId: string;
    sharedWith: ShareRecipient[];
    isSharedWithAll: boolean;
}

class AppointmentTypeShareService {
    async getShares(appointmentTypeId: string): Promise<AppointmentTypeShare[]> {
        try {
            const response = await apiClient.get<AppointmentTypeShare[]>(`/appointment-types/${appointmentTypeId}/shares`);
            return response.data;
        } catch (error) {
            console.error('Error fetching appointment type shares:', error);
            throw error;
        }
    }

    async createShare(appointmentTypeId: string, dto: CreateAppointmentTypeShareDto): Promise<AppointmentTypeShare> {
        try {
            const response = await apiClient.post<AppointmentTypeShare>(`/appointment-types/${appointmentTypeId}/shares`, dto);
            return response.data;
        } catch (error) {
            console.error('Error creating appointment type share:', error);
            throw error;
        }
    }

    async updateShare(appointmentTypeId: string, shareId: string, dto: UpdateAppointmentTypeShareDto): Promise<AppointmentTypeShare> {
        try {
            const response = await apiClient.put<AppointmentTypeShare>(`/appointment-types/${appointmentTypeId}/shares/${shareId}`, dto);
            return response.data;
        } catch (error) {
            console.error('Error updating appointment type share:', error);
            throw error;
        }
    }

    async deleteShare(appointmentTypeId: string, shareId: string): Promise<void> {
        try {
            await apiClient.delete(`/appointment-types/${appointmentTypeId}/shares/${shareId}`);
        } catch (error) {
            console.error('Error deleting appointment type share:', error);
            throw error;
        }
    }

    // Methods for managing user's own shares (My Account)
    async getMyShares(): Promise<MyAppointmentTypeShare[]> {
        try {
            const response = await apiClient.get<MyAppointmentTypeShare[]>('/my-appointment-type-shares');
            return response.data;
        } catch (error) {
            console.error('Error fetching my appointment type shares:', error);
            throw error;
        }
    }

    async updateMyShare(shareId: string, dto: UpdateMyAppointmentTypeShareDto): Promise<MyAppointmentTypeShare> {
        try {
            const response = await apiClient.put<MyAppointmentTypeShare>(`/my-appointment-type-shares/${shareId}`, dto);
            return response.data;
        } catch (error) {
            console.error('Error updating my appointment type share:', error);
            throw error;
        }
    }

    async removeMyShare(shareId: string): Promise<void> {
        try {
            await apiClient.delete(`/my-appointment-type-shares/${shareId}`);
        } catch (error) {
            console.error('Error removing my appointment type share:', error);
            throw error;
        }
    }

    // Methods for Appointment Access (manageable types with share info)
    async getManageableAppointmentTypes(): Promise<AppointmentTypeWithShares[]> {
        try {
            const response = await apiClient.get<AppointmentTypeWithShares[]>('/appointment-type-access');
            return response.data;
        } catch (error) {
            console.error('Error fetching manageable appointment types:', error);
            throw error;
        }
    }

    async shareAllWithBusinessUsers(canWrite: boolean): Promise<void> {
        try {
            await apiClient.post('/appointment-type-access/share-all', { canWrite });
        } catch (error) {
            console.error('Error sharing all appointment types:', error);
            throw error;
        }
    }
}

export const appointmentTypeShareService = new AppointmentTypeShareService(); 